import type { SsnCountry } from '../enums.js';
import { cnsCheckDigits, formatCnsId, normalizeCnsId } from './cns.js';

/**
 * Numéro de sécurité sociale **selon le pays qui le délivre** : matricule CNS luxembourgeois
 * (13 chiffres), NIR français (15 caractères, la Corse écrivant `2A`/`2B` en département), NISS
 * belge (11 chiffres), ou « autre » — texte libre, non vérifié.
 *
 * Porté de la V1 (`client/src/lib/ssn-validation.ts`), qui l'offrait déjà avec un sélecteur de
 * pays devant le champ : la V2.0 avait réduit le matricule au seul CNS, alors que la patientèle
 * d'un cabinet luxembourgeois est frontalière par nature. Le moteur du CNS reste `cns.ts` ;
 * ce fichier ajoute les deux voisins et le choix.
 *
 * Les erreurs sont des **codes**, pas des phrases : c'est l'écran qui les traduit, en trois
 * langues. Le serveur, lui, n'a besoin que de `valid`. La règle est la même des deux côtés,
 * puisqu'elle vit ici.
 */

/**
 * Ce qui cloche, en codes que l'écran traduit :
 * - `country_required` : un numéro sans pays — la V1 grisait le champ tant qu'aucun pays n'était
 *   choisi. Le serveur retombe sur `DEFAULT_SSN_COUNTRY` et ne le rend donc jamais ;
 * - `length` : mauvaise longueur, séparateurs retirés — 13 (LU), 15 (FR), 11 (BE), 1 à 40 (autre) ;
 * - `digits` : un caractère qui n'a rien à faire là ;
 * - `sex_digit` : NIR dont le premier chiffre n'est ni 1, ni 2, ni 7, ni 8 ;
 * - `year`, `month`, `day` : une borne de la date encodée ; `date` : une date qui n'existe pas
 *   (un 30 février) ou qui n'est pas encore arrivée ;
 * - `key` : la clé de contrôle ne correspond pas.
 */
export type SsnError =
  | 'country_required'
  | 'length'
  | 'digits'
  | 'sex_digit'
  | 'month'
  | 'day'
  | 'year'
  | 'date'
  | 'key';

export interface SsnValidation {
  readonly valid: boolean;
  readonly errors: readonly SsnError[];
  /**
   * Date de naissance encodée dans le numéro, `yyyy-MM-dd`, quand il est valide et la porte.
   * Le NIR n'encode pas le jour : on rend le premier du mois, et `dayReliable` est faux — l'écran
   * ne propose alors la date qu'avec un avertissement, comme la V1.
   */
  readonly birthDate: string | null;
  readonly dayReliable: boolean;
  /** Sexe encodé, quand le pays l'encode (NIR par le premier chiffre, NISS par le rang). */
  readonly sex: 'male' | 'female' | null;
  /** Le numéro groupé pour l'affichage, quand il est valide. */
  readonly formatted: string | null;
}

/** Longueur maximale stockée, séparateurs retirés. Large : « autre » est un texte libre. */
export const SSN_MAX_LENGTH = 40;

/**
 * Pays du numéro quand rien n'est dit : le Luxembourg. C'est le défaut de la colonne
 * `patients.ssn_country` — tous les matricules saisis avant le sélecteur étaient des CNS — et le
 * point de départ du formulaire.
 */
export const DEFAULT_SSN_COUNTRY: SsnCountry = 'LU';

/**
 * Les exemples de la V1, un par pays, à montrer dans le champ vide. Ce sont des **formes**, pas
 * des numéros : seul l'exemple français porte une clé juste, ceux du Luxembourg et de la Belgique
 * échouent à la clé (`ssn.test.ts` le constate). Gardés tels que la V1 les affichait.
 */
export const SSN_PLACEHOLDERS: Readonly<Record<SsnCountry, string>> = {
  LU: '1989 02 23 001 45',
  FR: '2 85 05 75 106 112 04',
  BE: '85.07.30-033-91',
  OTHER: '',
};

const MIN_BIRTH_YEAR = 1900;

/* -------------------------------------------------------------------------- */
/* Outils                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Retire espaces, points et tirets ; le NIR garde ses lettres de Corse, en majuscules. « Autre »
 * n'est que taillé : on ne sait pas ce qu'on retirerait.
 */
export function normalizeSsn(value: string, country: SsnCountry): string {
  const trimmed = value.trim();
  if (country === 'OTHER') return trimmed;
  return trimmed.replace(/[\s.-]/g, '').toUpperCase();
}

function invalid(errors: SsnError[]): SsnValidation {
  return { valid: false, errors, birthDate: null, dayReliable: true, sex: null, formatted: null };
}

/** `yyyy-MM-dd` si la date existe réellement (le 30 février n'existe pas), sinon `null`. */
function isoDate(year: number, month: number, day: number): string | null {
  if (year < MIN_BIRTH_YEAR || year > new Date().getFullYear()) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  const roundTrips =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return roundTrips ? date.toISOString().slice(0, 10) : null;
}

/** Année sur deux chiffres → quatre : au plus tard cette année, sinon le siècle précédent. */
function fullYearFrom(twoDigits: number): number {
  const currentYear = new Date().getFullYear();
  const century = Math.floor(currentYear / 100) * 100;
  return twoDigits <= currentYear % 100 ? century + twoDigits : century - 100 + twoDigits;
}

/* -------------------------------------------------------------------------- */
/* Luxembourg — CNS, 13 chiffres                                               */
/* -------------------------------------------------------------------------- */

/**
 * `AAAAMMJJ XXX C1C2`. Les trois bornes de la date ont chacune leur code, comme les trois
 * messages de la V1 : l'écran dit laquelle est fausse au lieu d'un « date invalide » global.
 */
function validateLuxembourg(value: string): SsnValidation {
  const clean = normalizeCnsId(value);
  if (clean.length !== 13) return invalid(['length']);
  if (!/^\d{13}$/.test(clean)) return invalid(['digits']);
  const year = Number(clean.slice(0, 4));
  const month = Number(clean.slice(4, 6));
  const day = Number(clean.slice(6, 8));
  const errors: SsnError[] = [];
  if (year < MIN_BIRTH_YEAR || year > new Date().getFullYear()) errors.push('year');
  if (month < 1 || month > 12) errors.push('month');
  if (day < 1 || day > 31) errors.push('day');
  if (errors.length > 0) return invalid(errors);

  const birthDate = isoDate(year, month, day);
  if (birthDate === null) return invalid(['date']);
  const keys = cnsCheckDigits(clean.slice(0, 11));
  // Garde de typage : treize chiffres vérifiés plus haut donnent une base de onze.
  if (keys === null) return invalid(['digits']);
  if (Number(clean.charAt(11)) !== keys.luhn || Number(clean.charAt(12)) !== keys.verhoeff) {
    return invalid(['key']);
  }
  return {
    valid: true,
    errors: [],
    birthDate,
    dayReliable: true,
    sex: null,
    formatted: formatCnsId(clean),
  };
}

/* -------------------------------------------------------------------------- */
/* France — NIR, 15 caractères                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `S AA MM DD CCC OOO KK` : sexe (1 homme, 2 femme, 7 et 8 provisoires), année et mois de
 * naissance, département (`2A`/`2B` pour la Corse, comptés 19 et 18 dans la clé), commune, rang,
 * clé `97 − (13 premiers mod 97)`. Le jour de naissance n'y est pas.
 */
function validateFrance(value: string): SsnValidation {
  const clean = normalizeSsn(value, 'FR');
  if (clean.length !== 15) return invalid(['length']);
  const errors: SsnError[] = [];
  const sexDigit = clean.charAt(0);
  if (!['1', '2', '7', '8'].includes(sexDigit)) errors.push('sex_digit');
  const month = Number(clean.slice(3, 5));
  if (!/^\d{2}$/.test(clean.slice(3, 5)) || month < 1 || month > 12) errors.push('month');

  const department = clean.slice(5, 7);
  let base = clean.slice(0, 13);
  if (department === '2A') base = `${clean.slice(0, 5)}19${clean.slice(7, 13)}`;
  else if (department === '2B') base = `${clean.slice(0, 5)}18${clean.slice(7, 13)}`;
  if (!/^\d{13}$/.test(base) || !/^\d{2}$/.test(clean.slice(13, 15))) {
    return invalid([...errors, 'digits']);
  }
  if (errors.length > 0) return invalid(errors);

  // Treize chiffres tiennent dans un `number` sans perte : on reste sous 2^53.
  const expectedKey = 97 - (Number(base) % 97);
  if (Number(clean.slice(13, 15)) !== expectedKey) return invalid(['key']);

  const birthDate = isoDate(fullYearFrom(Number(clean.slice(1, 3))), month, 1);
  if (birthDate === null) return invalid(['date']);
  return {
    valid: true,
    errors: [],
    birthDate,
    dayReliable: false,
    sex: sexDigit === '1' || sexDigit === '7' ? 'male' : 'female',
    formatted: `${clean.slice(0, 1)} ${clean.slice(1, 3)} ${clean.slice(3, 5)} ${clean.slice(5, 7)} ${clean.slice(7, 10)} ${clean.slice(10, 13)} ${clean.slice(13, 15)}`,
  };
}

/* -------------------------------------------------------------------------- */
/* Belgique — NISS, 11 chiffres                                                */
/* -------------------------------------------------------------------------- */

/**
 * `AA.MM.JJ-SSS-KK` : date de naissance, rang (impair homme, pair femme), clé `97 − (9 premiers
 * mod 97)` — les 9 premiers précédés d'un `2` pour qui est né à partir de 2000. C'est la clé qui
 * dit le siècle : on essaie les deux.
 */
function validateBelgium(value: string): SsnValidation {
  const clean = normalizeSsn(value, 'BE');
  if (clean.length !== 11) return invalid(['length']);
  if (!/^\d{11}$/.test(clean)) return invalid(['digits']);
  const errors: SsnError[] = [];
  const month = Number(clean.slice(2, 4));
  const day = Number(clean.slice(4, 6));
  if (month < 1 || month > 12) errors.push('month');
  if (day < 1 || day > 31) errors.push('day');
  if (errors.length > 0) return invalid(errors);

  const base = Number(clean.slice(0, 9));
  const key = Number(clean.slice(9, 11));
  let year: number;
  if (key === 97 - ((2_000_000_000 + base) % 97)) year = 2000 + Number(clean.slice(0, 2));
  else if (key === 97 - (base % 97)) year = 1900 + Number(clean.slice(0, 2));
  else return invalid(['key']);

  const birthDate = isoDate(year, month, day);
  if (birthDate === null) return invalid(['date']);
  return {
    valid: true,
    errors: [],
    birthDate,
    dayReliable: true,
    sex: Number(clean.charAt(8)) % 2 === 1 ? 'male' : 'female',
    formatted: `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4, 6)}-${clean.slice(6, 9)}-${clean.slice(9, 11)}`,
  };
}

/* -------------------------------------------------------------------------- */
/* Choix par pays                                                              */
/* -------------------------------------------------------------------------- */

/** « Autre » : on garde ce qui est écrit, borné, sans rien en déduire. */
function validateOther(value: string): SsnValidation {
  const clean = value.trim();
  if (clean.length === 0 || clean.length > SSN_MAX_LENGTH) return invalid(['length']);
  return {
    valid: true,
    errors: [],
    birthDate: null,
    dayReliable: true,
    sex: null,
    formatted: clean,
  };
}

export function validateSsn(value: string, country: SsnCountry): SsnValidation {
  switch (country) {
    case 'LU':
      return validateLuxembourg(value);
    case 'FR':
      return validateFrance(value);
    case 'BE':
      return validateBelgium(value);
    case 'OTHER':
      return validateOther(value);
  }
}

/** Le numéro groupé pour l'affichage ; rendu tel quel s'il n'est pas valide. */
export function formatSsn(value: string, country: SsnCountry): string {
  return validateSsn(value, country).formatted ?? value;
}

/** Date de naissance `yyyy-MM-dd` encodée dans le numéro, ou `null`. Voir `dayReliable`. */
export function ssnBirthDate(value: string, country: SsnCountry): string | null {
  return validateSsn(value, country).birthDate;
}

/**
 * Ce qui cloche entre un numéro et son pays, à poser sur le champ `ssn` — au service côté
 * serveur, au formulaire côté écran, parce que la règle porte sur **deux champs** à la fois, ce
 * qu'un schéma de champ ne sait pas dire. `null` quand tout va, ou quand il n'y a pas de numéro :
 * un pays sans numéro est libre.
 */
export function ssnIssue(input: {
  readonly ssn: string | null | undefined;
  readonly ssnCountry: SsnCountry | null | undefined;
}): SsnError | null {
  if (input.ssn === null || input.ssn === undefined || input.ssn.trim() === '') return null;
  if (input.ssnCountry === null || input.ssnCountry === undefined) return 'country_required';
  const result = validateSsn(input.ssn, input.ssnCountry);
  return result.valid ? null : (result.errors[0] ?? 'digits');
}

/**
 * Le pays qu'un numéro désigne **à lui seul** : celui des trois registres vérifiés — CNS, NIR,
 * NISS — dont il passe la clé. Leurs longueurs diffèrent (13, 15, 11), donc un numéro valide n'en
 * reconnaît jamais deux. `null` quand aucun ne le reconnaît : on ne devine pas « autre ».
 *
 * Sert là où le pays n'est pas dit, un fichier importé sans colonne de pays par exemple. Le
 * formulaire, lui, demande le pays avant le numéro, comme la V1.
 */
export function detectSsnCountry(value: string): Exclude<SsnCountry, 'OTHER'> | null {
  const checked = ['LU', 'FR', 'BE'] as const;
  return checked.find((country) => validateSsn(value, country).valid) ?? null;
}
